import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { availablePageSizes } from 'config';
import { invoiceAggregationAPI } from 'redux/invoiceAggregationAPI';
import { chain, difference, map } from 'lodash';
import { createPaginationAdapter } from '@payer-admin-ui/components-v2/src/state/paginationAdapter';
import { CountryCode } from '@payer-admin-ui/api/src/invoices/models/CountryCode';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { PageResponse } from '@payer-admin-ui/api/src/utils/repository/PageResponse';
import { LocalDate } from '@payer-admin-ui/api/src/external/java/LocalDate';
import {
  AppEntityState,
  FetchingEntityState,
  ListViewWithFilterAndMultiSortState,
  SingleSortType,
  SingleViewState,
} from '@payer-admin-ui/components-v2/src/types/state';
import { createFilterAdapter } from '@payer-admin-ui/components-v2/src/state/filterAdapter';
import { GetInvoiceStatementBasicsResponse } from '@payer-admin-ui/api/src/ui/invoicestatements/responses/GetInvoiceStatementBasicsResponse';
import {
  GetInvoiceStatementDetailsResponse,
  InvoiceStatementDetailsCreditUsage,
  InvoiceStatementDetailsInvoice,
  InvoiceStatementDetailsPayment,
} from '@payer-admin-ui/api/src/ui/invoicestatements/responses/GetInvoiceStatementDetailsResponse';
import { PaymentStatus } from '@payer-admin-ui/api/src/invoices/models/PaymentStatus';
import { createMultiColumnSortingAdapter } from '@payer-admin-ui/components-v2/src/state/sortingAdapter';
import { patch } from '@payer-admin-ui/components-v2/src/functions/lodashExtensions';
import { MerchantAdminState } from '../../../redux/store';
import { updateTableSettings, clearTableSettings } from '../../../redux/userSettings';

export const INVOICE_AGGREGATIONS_TABLE_SETTINGS_KEY = 'invoiceAggregations';

export enum InvoiceAggregationsSortableColumn {
  invoiceAggregationNumber = 'invoiceAggregationNumber',
  currencyCode = 'currencyCode',
}

export const fetchInvoiceAggregationsPage = createAsyncThunk<
  PageResponse<GetInvoiceStatementBasicsResponse>,
  void,
  { state: MerchantAdminState }
>('invoiceAggregations/fetchPage', async (_, { getState }) => {
  const { page, pageSize, appliedFilters, sortState } = getState().invoiceAggregations.listView;
  const sort = (sortState || []).filter(s => s.sortDirection).map(s => `${s.column}|${s.sortDirection.toUpperCase()}`);
  return await invoiceAggregationAPI.fetchPage({ page, pageSize, appliedFilters, sort });
});

export const fetchInvoiceAggregation = createAsyncThunk<GetInvoiceStatementDetailsResponse, number>(
  'invoiceAggregations/fetchByInvoiceAggregationNumber',
  async invoiceAggregationNumber => {
    return await invoiceAggregationAPI.fetchDetails({ invoiceAggregationNumber });
  },
);

export const applyInvoiceAggregationFilters = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'invoiceAggregations/applyFilters',
  async (_, { getState, dispatch }) => {
    dispatch(invoiceAggregationsSlice.actions.applyFilters());
    dispatch(
      updateTableSettings({
        component: INVOICE_AGGREGATIONS_TABLE_SETTINGS_KEY,
        update: { appliedFilters: getState().invoiceAggregations.listView.appliedFilters },
      }),
    );
  },
);

export const setInvoiceAggregationSort = createAsyncThunk<
  void,
  SingleSortType<InvoiceAggregationsSortableColumn>,
  { state: MerchantAdminState }
>('invoiceAggregations/setSort', async (sort, { getState, dispatch }) => {
  dispatch(invoiceAggregationsSlice.actions.setSort(sort));
  const sortState = getState().invoiceAggregations.listView.sortState;
  dispatch(
    updateTableSettings({
      component: INVOICE_AGGREGATIONS_TABLE_SETTINGS_KEY,
      update: { sortOrders: sortState },
    }),
  );
  dispatch(fetchInvoiceAggregationsPage());
});

export const syncUserSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'invoiceAggregations/syncUserSettings',
  async (_, { getState, dispatch }) => {
    const settings = getState().userSettings?.tableSettings?.[INVOICE_AGGREGATIONS_TABLE_SETTINGS_KEY];
    const pageSize = settings?.pageSize;
    const sortModel = settings?.sortOrders;
    const appliedFilters = settings?.appliedFilters;
    if (pageSize) {
      dispatch(setPageSize(pageSize));
    }
    if (sortModel) {
      dispatch(setSorts(sortModel as SingleSortType<InvoiceAggregationsSortableColumn>[]));
    }
    if (appliedFilters) {
      dispatch(
        setFilter({
          ...getState().invoiceAggregations.listView.filters,
          ...appliedFilters,
        }),
      );
      dispatch(invoiceAggregationsSlice.actions.applyFilters());
    }
    dispatch(setInited());
  },
);

export const clearInvoiceAggregationsTableSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'invoiceAggregations/clearTableSettings',
  async (_, { dispatch }) => {
    dispatch(setFilter(initialFilters));
    dispatch(invoiceAggregationsSlice.actions.applyFilters());
    dispatch(invoiceAggregationsSlice.actions.resetSort());
    dispatch(setPageSize(availablePageSizes[0]));
    dispatch(
      clearTableSettings({
        component: INVOICE_AGGREGATIONS_TABLE_SETTINGS_KEY,
        customDefaultSettings: {
          appliedFilters: initialFilters,
        },
      }),
    );
  },
);

export type InvoiceAggregationsFilters = {
  countryCodes: CountryCode[];
  currencyCodes: CurrencyCode[];
  paymentStatuses: PaymentStatus[];
  deliveryTimeStart?: LocalDate;
  deliveryTimeEnd?: LocalDate;
  search: string;
};

export const emptyIAFilters: InvoiceAggregationsFilters = {
  countryCodes: [],
  currencyCodes: [],
  paymentStatuses: [],
  search: '',
};

export type InvoiceAggregationsStateData = GetInvoiceStatementBasicsResponse & {
  payments?: InvoiceStatementDetailsPayment[];
  invoices?: InvoiceStatementDetailsInvoice[];
  creditUsages?: InvoiceStatementDetailsCreditUsage[];
};

export type InvoiceAggregationsGlobalState = AppEntityState<InvoiceAggregationsStateData, number> &
  SingleViewState<number> &
  ListViewWithFilterAndMultiSortState<number, InvoiceAggregationsFilters, InvoiceAggregationsSortableColumn> & {
    listView: { inited: boolean };
  };

export type InvoiceAggregationEntity = FetchingEntityState<InvoiceAggregationsStateData, number>;

const paginationAdapter = createPaginationAdapter<InvoiceAggregationsGlobalState>({
  selectState: state => state.listView,
});

const filterAdapter = createFilterAdapter<InvoiceAggregationsGlobalState, InvoiceAggregationsFilters>({
  selectState: state => state.listView,
  emptyFilters: emptyIAFilters,
  reset: paginationAdapter.reset,
});

const sortingAdapter = createMultiColumnSortingAdapter<
  InvoiceAggregationsGlobalState,
  InvoiceAggregationsSortableColumn
>({
  selectState: state => state.listView,
  reset: paginationAdapter.reset,
});

const SINGLE_CACHE_SIZE = 10;
const entityAdapter = createEntityAdapter<InvoiceAggregationEntity>();
const pruneCache = (state: InvoiceAggregationsGlobalState) => {
  entityAdapter.removeMany(state, difference(state.ids, state.singleView.cachedIds, state.listView.ids));
};

const upsertOnePruneCache = (state: InvoiceAggregationsGlobalState, entity: InvoiceAggregationEntity) => {
  entityAdapter.upsertOne(state, entity);
  state.singleView.cachedIds = chain([entity.id]).union(state.singleView.cachedIds).take(SINGLE_CACHE_SIZE).value();
  pruneCache(state);
};

const initialFilters: InvoiceAggregationsFilters = {
  ...emptyIAFilters,
};

const initialState: InvoiceAggregationsGlobalState = entityAdapter.getInitialState({
  singleView: {
    cachedIds: [],
  },
  listView: {
    isFetching: false,
    error: null,
    inited: false,
    ids: [],
    page: 0, // zero-indexed in state, one-indexed in UI
    pageSize: availablePageSizes[0],
    totalCount: null,
    filters: initialFilters,
    appliedFilters: initialFilters,
    sortState: undefined,
  },
});

const invoiceAggregationsSlice = createSlice({
  name: 'invoiceAggregations',
  initialState,
  reducers: {
    resetError: state => {
      state.listView.error = null;
    },
    resetEntityError: (state, { payload }: PayloadAction<number>) => {
      entityAdapter.updateOne(state, { id: payload, changes: { error: null } });
    },
    setInited: state => {
      state.listView.inited = true;
    },
    setPage: paginationAdapter.setPage,
    setPageSize: paginationAdapter.setPageSize,
    setFilter: filterAdapter.setFilter,
    clearFilter: filterAdapter.clearFilter,
    clearAllFilters: filterAdapter.clearAllFilters,
    applyFilters: filterAdapter.applyFilters,
    setSort: sortingAdapter.setSort,
    setSorts: sortingAdapter.setSorts,
    resetSort: sortingAdapter.resetSort,
  },
  extraReducers: builder => {
    builder.addCase(fetchInvoiceAggregation.pending, (state, { meta: { arg } }) => {
      upsertOnePruneCache(state, { id: arg, isFetching: true, error: null });
    });
    builder.addCase(fetchInvoiceAggregation.fulfilled, (state, action) => {
      entityAdapter.upsertOne(state, {
        id: action.meta.arg,
        entity: {
          ...action.payload.invoiceStatement,
          payments: action.payload.payments,
          invoices: action.payload.invoices,
          creditUsages: action.payload.creditUsages,
        },
        isFetching: false,
      });
    });
    builder.addCase(fetchInvoiceAggregation.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, {
        id: arg,
        entity: undefined,
        isFetching: false,
        error,
      });
    });
    builder.addCase(fetchInvoiceAggregationsPage.pending, (state, _) => {
      patch(state.listView, { isFetching: true, error: null });
    });
    builder.addCase(fetchInvoiceAggregationsPage.fulfilled, (state, { payload: { contents, totalCount } }) => {
      const invoiceAggregations = normalize(contents);
      patch(state.listView, { ids: map(invoiceAggregations, 'id'), totalCount, isFetching: false });
      entityAdapter.upsertMany(state, invoiceAggregations);
      pruneCache(state);
    });
    builder.addCase(fetchInvoiceAggregationsPage.rejected, (state, { error }) => {
      patch(state.listView, { ids: [], totalCount: null, isFetching: false, error });
      pruneCache(state);
    });
  },
});

const normalize = (invoiceAggregations: GetInvoiceStatementBasicsResponse[]) =>
  map(invoiceAggregations, invoiceAggregation => ({
    id: invoiceAggregation.invoiceAggregationNumber,
    entity: invoiceAggregation,
    isFetching: false,
    error: null,
  }));

export const {
  actions: {
    resetError,
    resetEntityError,
    setPage,
    setPageSize,
    setFilter,
    clearFilter,
    clearAllFilters,
    applyFilters,
    setSorts,
    resetSort,
    setInited,
  },
  reducer: invoiceAggregationsReducer,
} = invoiceAggregationsSlice;

export const invoiceAggregationSelectors = entityAdapter.getSelectors<MerchantAdminState>(
  state => state.invoiceAggregations,
);
